import type { ReactNode } from 'react'
import { Anchor, Paper, ScrollArea, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { useAppTranslation } from 'hooks/useTranslation'
import type { ApplicationRow, DeskRawItem } from 'supabase/desk'

/**
 * Allowlist, not a blocklist (twin of ApplicationEdit's `isHttpsUrl`): a
 * prefix test on the one scheme we accept cannot be beaten by casing,
 * whitespace or control characters the way a "javascript:" blocklist can.
 */
export const isHttpsUrl = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.startsWith('https://')

const formatDay = (value: string | null | undefined): string | null => {
  if (!value) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null
}

export type SourcePostPanelProps = {
  rawItem: DeskRawItem | null | undefined
  application: Pick<ApplicationRow, 'source' | 'source_url'>
  /** The image strip (UI-SPEC §D puts it inside this panel). */
  children?: ReactNode
}

/**
 * "Source post" (UI-SPEC §D): the post the editor read, so Dominique checks
 * the candidate against the original without leaving the desk.
 *
 * The post text is untrusted page text: it is a plain `Text` child with
 * `pre-wrap`, never HTML or Markdown (T-33-56). The original link is shown only
 * for an https URL and opens with `noopener noreferrer`.
 */
export const SourcePostPanel = ({ rawItem, application, children }: SourcePostPanelProps) => {
  const { t } = useAppTranslation()
  const source = rawItem?.source ?? application.source
  const isPaste = source === 'paste'
  const text = rawItem?.content?.trim() ? rawItem.content : null
  const url = [rawItem?.source_url, application.source_url].find(isHttpsUrl) ?? null
  const day = formatDay(rawItem?.posted_at ?? rawItem?.first_seen_at)

  return (
    <Paper withBorder p={16} bg='gray.0'>
      <Stack gap={8}>
        <Text size='md' fw={600} lh={1.3}>
          {t('desk.candidate.source_post')}
        </Text>
        {text ? (
          <ScrollArea.Autosize mah={360}>
            <Text size='sm' style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {text}
            </Text>
          </ScrollArea.Autosize>
        ) : (
          <Text size='sm' c='dimmed'>
            {t('desk.candidate.no_text')}
          </Text>
        )}
        {url && (
          <Anchor href={url} target='_blank' rel='noopener noreferrer' size='sm'>
            {t('desk.candidate.open_original')}
          </Anchor>
        )}
        {day && (
          <Text size='xs' lh={1.4} c='dimmed'>
            {t(isPaste ? 'desk.candidate.pasted_on' : 'desk.candidate.posted_on', { date: day })}
          </Text>
        )}
        {children}
      </Stack>
    </Paper>
  )
}
